import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { Divider } from "@/react/components/search/Divider";
import { LoaderRow } from "@/react/components/shared/LoaderRow";
import { Button } from "@/react/components/shared/uikit/Button";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { CommunityMember } from "@/react/types";
import type { Person } from "@/react/types/AudienceList";
import { ExpandableAccordionSkeleton } from "./ExpandableAccordionSkeleton";
import { ExpandableListItem } from "./ExpandableListItem";

interface ExpandableAccordionProps {
  className?: string;
  fetchNextPage?: () => void;
  hasMore?: boolean;
  incrementCount?: number;
  initialVisibleCount?: number;
  isFetchingNextPage?: boolean;
  isLoading: boolean;
  items: ExpandableAccordionItem[];
  title: string;
  totalCount?: number;
  totalCountClassName?: string;
}

export interface ExpandableAccordionItem {
  id: string;
  person: Person;
  type: "recommendation" | "connection";
}

export const ExpandableAccordion = ({
  title,
  items,
  totalCount,
  isLoading,
  hasMore = false,
  fetchNextPage,
  isFetchingNextPage = false,
  initialVisibleCount = 2,
  incrementCount = 2,
  className,
  totalCountClassName,
}: ExpandableAccordionProps) => {
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);

  const handleShowMore = () => {
    if (fetchNextPage && hasMore) {
      fetchNextPage();
      setVisibleCount(prev => prev + incrementCount);
    }
  };

  const visibleItems = items.slice(0, visibleCount);
  const hasMoreToShow = Boolean(fetchNextPage && hasMore);

  if (isLoading) {
    return (
      <ExpandableAccordionSkeleton
        className={className}
        title={title}
        initialVisibleCount={initialVisibleCount}
      />
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="border-primary flex flex-col rounded-2xl border">
      <div className="border-primary flex items-center gap-x-2 border-b px-6 py-3">
        <Typography.LabelMd weight="semibold">{title}</Typography.LabelMd>
        {!!totalCount && totalCount > 0 && (
          <span
            className={classNames(
              "bg-v2-danger-100 text-v2-danger flex h-[18px] items-center justify-center rounded-3xl px-1.5 py-0.5 text-center text-xs font-medium",
              totalCountClassName,
            )}
          >
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </div>

      <ul className="flex list-none flex-col">
        {visibleItems.map((item, index) => (
          <div key={item?.person.public_uid}>
            <ExpandableListItem
              communityMember={item.person as CommunityMember}
              item={item}
            />
            {index < visibleItems.length - 1 && <Divider />}
          </div>
        ))}
      </ul>

      {isFetchingNextPage && <LoaderRow />}

      {hasMoreToShow && !isFetchingNextPage && (
        <div className="bg-secondary flex justify-center rounded-b-2xl">
          <Button
            variant="tertiary"
            onClick={handleShowMore}
            className="w-full !rounded-none !rounded-b-2xl"
          >
            {t("show_more")}
          </Button>
        </div>
      )}
    </div>
  );
};
